// Traduit des indicateurs (tendance, adhérence, plateau…) en phrases d'insight.
import { round } from './statistics';
import type { TrainingPhase } from './types';

export type InsightTone = 'good' | 'warn' | 'info';

export interface Insight {
  tone: InsightTone;
  text: string;
}

export interface InsightContext {
  /** kg/semaine, négative = perte. */
  trendPerWeek: number;
  /** Phase d'entraînement : oriente le sens attendu de la tendance. Défaut 'cut'. */
  trainingPhase?: TrainingPhase;
  targetProgressPct: number;
  toTargetKg: number;
  plateau: boolean;
  /** null si non applicable. */
  etaDays: number | null;
  adherencePct: number;
  currentStreak: number;
  proteinAvgG: number;
  proteinTargetG: number;
  /** Optionnels : absents si l'hydratation n'est pas suivie. */
  waterTodayMl?: number;
  waterTargetMl?: number;
}

export function buildInsights(ctx: InsightContext): Insight[] {
  const out: Insight[] = [];

  if (Number.isFinite(ctx.trendPerWeek)) {
    const t = round(ctx.trendPerWeek, 2);
    const phase = ctx.trainingPhase ?? 'cut';
    const losing = t <= -0.05;
    const gaining = t >= 0.05;
    const stable = !losing && !gaining;
    if (phase === 'bulk') {
      // En prise de masse, la hausse est l'objectif.
      if (gaining) out.push({ tone: 'good', text: `Tu prends environ +${t} kg/semaine.` });
      else if (losing)
        out.push({
          tone: 'warn',
          text: `Tendance à la baisse : ${t} kg/semaine (objectif : prise).`,
        });
      else out.push({ tone: 'info', text: 'Ton poids stagne — surplus peut-être insuffisant.' });
    } else if (phase === 'recovery') {
      // En récupération, la stabilité est visée.
      if (stable) out.push({ tone: 'good', text: 'Poids stable — cohérent avec la récupération.' });
      else if (losing)
        out.push({ tone: 'warn', text: `Tu perds ${Math.abs(t)} kg/semaine en récupération.` });
      else out.push({ tone: 'info', text: `Tendance à la hausse : +${t} kg/semaine.` });
    } else {
      // Sèche (défaut) : la baisse est l'objectif.
      if (losing) out.push({ tone: 'good', text: `Tu perds environ ${Math.abs(t)} kg/semaine.` });
      else if (gaining)
        out.push({ tone: 'warn', text: `Tendance à la hausse : +${t} kg/semaine.` });
      else out.push({ tone: 'info', text: 'Ton poids est globalement stable.' });
    }
  }

  if (ctx.plateau) {
    out.push({
      tone: 'warn',
      text: 'Plateau détecté : le poids stagne sur les dernières semaines.',
    });
  }

  if (ctx.etaDays != null && ctx.etaDays > 0 && ctx.toTargetKg > 0) {
    const weeks = Math.round(ctx.etaDays / 7);
    out.push({
      tone: 'info',
      text: `À ce rythme, cible atteinte dans ~${weeks} semaine${weeks > 1 ? 's' : ''}.`,
    });
  }

  if (ctx.targetProgressPct >= 100) {
    out.push({ tone: 'good', text: 'Objectif de poids cible atteint 🎉' });
  } else if (ctx.targetProgressPct >= 80) {
    out.push({
      tone: 'good',
      text: `Presque à ta cible (${round(ctx.targetProgressPct)} % du chemin).`,
    });
  }

  if (Number.isFinite(ctx.adherencePct)) {
    if (ctx.adherencePct >= 80) {
      out.push({
        tone: 'good',
        text: `Belle régularité : ${round(ctx.adherencePct)} % des jours conformes à l'objectif.`,
      });
    } else if (ctx.adherencePct > 0 && ctx.adherencePct < 50) {
      out.push({
        tone: 'warn',
        text: `Objectif tenu ${round(ctx.adherencePct)} % des jours seulement.`,
      });
    }
  }

  if (ctx.currentStreak >= 3) {
    out.push({ tone: 'good', text: `Série en cours : ${ctx.currentStreak} jours conformes.` });
  }

  if (
    ctx.proteinTargetG > 0 &&
    ctx.proteinAvgG > 0 &&
    ctx.proteinAvgG < 0.85 * ctx.proteinTargetG
  ) {
    out.push({
      tone: 'warn',
      text: `Protéines sous l'objectif (${round(ctx.proteinAvgG)} g vs ${round(ctx.proteinTargetG)} g).`,
    });
  }

  if (ctx.waterTargetMl != null && ctx.waterTargetMl > 0 && ctx.waterTodayMl != null) {
    if (ctx.waterTodayMl >= ctx.waterTargetMl) {
      out.push({ tone: 'good', text: 'Objectif d’hydratation du jour atteint 💧' });
    } else if (ctx.waterTodayMl > 0 && ctx.waterTodayMl < 0.5 * ctx.waterTargetMl) {
      out.push({
        tone: 'info',
        text: `Hydratation en retard : ${round(ctx.waterTodayMl)} ml sur ${round(ctx.waterTargetMl)} ml.`,
      });
    }
  }

  return out;
}
